class FreeShippingGoal extends HTMLElement {
  static formatMoney(cents, formatString) {
    if (typeof cents === "string") {
      cents = cents.replace(".", "");
    }

    const value = parseInt(cents, 10);
    if (!isFinite(value)) {
      return "";
    }

    const format = formatString || "${{amount}}";
    const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    const match = format.match(placeholderRegex);

    if (!match) {
      return format;
    }

    const formatWithDelimiters = (
      number,
      precision = 2,
      thousands = ",",
      decimal = "."
    ) => {
      const fixed = (number / 100.0).toFixed(precision);
      const parts = fixed.split(".");
      const dollarsAmount = parts[0].replace(
        /(\d)(?=(\d\d\d)+(?!\d))/g,
        "$1" + thousands
      );
      const centsAmount = parts[1] ? decimal + parts[1] : "";
      return dollarsAmount + centsAmount;
    };

    let formattedValue = "";

    switch (match[1]) {
      case "amount":
        formattedValue = formatWithDelimiters(value, 2);
        break;
      case "amount_no_decimals":
        formattedValue = formatWithDelimiters(value, 0);
        break;
      case "amount_with_comma_separator":
        formattedValue = formatWithDelimiters(value, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = formatWithDelimiters(value, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = formatWithDelimiters(value, 2, "'", ".");
        break;
      case "amount_with_space_separator":
        formattedValue = formatWithDelimiters(value, 2, " ", ",");
        break;
      case "amount_no_decimals_with_space_separator":
        formattedValue = formatWithDelimiters(value, 0, " ", ",");
        break;
      default:
        formattedValue = formatWithDelimiters(value, 2);
    }

    return format.replace(placeholderRegex, formattedValue);
  }

  constructor() {
    super();
    this.handleCartChange = this.handleCartChange.bind(this);
  }

  connectedCallback() {
    this.messageEl = this.querySelector("[data-free-shipping-message]");
    this.progressBar = this.querySelector("[data-free-shipping-progress]");
    this.progressBarContainer = this.querySelector(
      "[data-free-shipping-progress-container]"
    );
    document.addEventListener("cart:change", this.handleCartChange);
    this.update();
  }

  disconnectedCallback() {
    document.removeEventListener("cart:change", this.handleCartChange);
  }

  getCurrencyRate() {
    const rate =
      window.Shopify && window.Shopify.currency && window.Shopify.currency.rate;
    const parsed = parseFloat(rate);
    return isFinite(parsed) && parsed > 0 ? parsed : 1;
  }

  getThresholdCents() {
    const baseThreshold = parseFloat(this.dataset.threshold);
    if (!isFinite(baseThreshold) || baseThreshold <= 0) {
      return 0;
    }

    const rate = this.getCurrencyRate();
    return Math.round(baseThreshold * 100 * rate);
  }

  getCartTotalCents(cart) {
    if (cart && typeof cart.total_price === "number") {
      return cart.total_price;
    }

    const fromDataset = parseInt(this.dataset.cartTotal, 10);
    return isFinite(fromDataset) ? fromDataset : 0;
  }

  getMoneyFormat() {
    return (
      this.dataset.moneyFormat ||
      (window.theme && window.theme.moneyFormat) ||
      "${{amount}}"
    );
  }

  handleCartChange(event) {
    if (event.detail && event.detail.cart) {
      this.update(event.detail.cart);
    }
  }

  update(cart) {
    const threshold = this.getThresholdCents();
    if (threshold <= 0) {
      this.hidden = true;
      return;
    }

    const total = this.getCartTotalCents(cart);
    if (total <= 0) {
      this.hidden = true;
      return;
    }

    this.hidden = false;
    this.dataset.cartTotal = String(total);

    const remaining = Math.max(0, threshold - total);
    const isComplete = total >= threshold;
    const progress = Math.min(100, Math.max(0, (total / threshold) * 100));
    const progressRounded = Math.round(progress);

    const messageTemplate = isComplete
      ? this.dataset.successMessage
      : this.dataset.message;
    const formattedAmount = FreeShippingGoal.formatMoney(
      remaining,
      this.getMoneyFormat()
    );
    const message = (messageTemplate || "").replace(
      "[amount]",
      formattedAmount
    );

    if (this.messageEl) {
      this.messageEl.textContent = message;
    }

    if (this.progressBar) {
      this.progressBar.style.width = `${progressRounded}%`;
    }

    if (this.progressBarContainer) {
      this.progressBarContainer.setAttribute(
        "aria-valuenow",
        String(progressRounded)
      );
      this.progressBarContainer.setAttribute("aria-label", message);
    }
  }
}

if (!customElements.get("free-shipping-goal")) {
  customElements.define("free-shipping-goal", FreeShippingGoal);
}
